// src/api/features/products/mutations.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createProduct, updateProduct, deleteProduct } from "./api";
import { ProductsKeys } from "./queries";

import type {
  ProductWithConfig,
  CreateProductPayload,
  UpdateProductPayload,
} from "./types";

/* ======================================================
 * CREATE – POST /products
 * ====================================================== */

export function useCreateProductMutation() {
  const qc = useQueryClient();

  return useMutation<ProductWithConfig, Error, CreateProductPayload>({
    mutationFn: (payload) => createProduct(payload),

    onSuccess(created) {
      // Cache detail for the new product
      qc.setQueryData<ProductWithConfig>(
        ProductsKeys.detail(created.id),
        created
      );

      // Invalidate all lists so they refetch with the new item
      qc.invalidateQueries({ queryKey: ProductsKeys.all });
    },
  });
}

/* ======================================================
 * UPDATE – PUT /products/:id
 * ====================================================== */

export function useUpdateProductMutation(id: number | string) {
  const qc = useQueryClient();

  return useMutation<ProductWithConfig, Error, UpdateProductPayload>({
    mutationFn: (payload) => updateProduct(id, payload),

    onSuccess(updated) {
      // Update detail cache
      qc.setQueryData<ProductWithConfig>(
        ProductsKeys.detail(id),
        updated
      );

      // Refresh all lists so any list UIs stay in sync
      qc.invalidateQueries({ queryKey: ProductsKeys.all });
    },
  });
}

/* ======================================================
 * DELETE – DELETE /products/:id
 * ====================================================== */

export function useDeleteProductMutation() {
  const qc = useQueryClient();

  return useMutation<{ message: string } | void, Error, number | string>({
    mutationFn: (id) => deleteProduct(id),

    onSuccess(_res, id) {
      // Drop detail cache for this product
      qc.removeQueries({ queryKey: ProductsKeys.detail(id) });

      // Refresh lists
      qc.invalidateQueries({ queryKey: ProductsKeys.all });
    },
  });
}
